import {
	AxisCode,
	ClassifiedObjectType,
	FILE_ALLOWED_CONTENT_TYPES,
	FILE_MAX_SIZE_BYTES,
	FileType,
	resolveContentType,
} from '@atlas/types'
import type {
	CampaignUploadExecutionOptions,
	CampaignUploadFailureStep,
	CampaignUploadInput,
	CampaignUploadOutcome,
	CampaignUploadPort,
} from '@/domain/campaign-ports'
import { campaignService } from '@/services/campaign.service'
import { classificationApi } from '@/services/classification.service'
import { isEmptyUploadSize } from '@/upload/upload-precheck'
import { uploaderService } from '@/upload/uploader/uploader-service'
import {
	mapUploadClassificationFailure,
	mapUploadConfirmationFailure,
	mapUploadInitiationFailure,
	mapUploadTransferFailure,
} from './campaign-upload-error'

// HTTP-backed CampaignUploadPort. One opaque page-facing operation over three
// real stages:
//
//   1. the shared upload seam (File creation, presign, PUT, confirmation)
//   2. POST /classify/batch
//   3. POST /campaigns/{id}/files
//
// The seam owns everything between creating the File and confirming it, so this
// adapter no longer orchestrates those steps itself and issues exactly one
// confirmation. Classification sits between the seam and association because
// the backend requires it: associating a File with no classification anchor
// fails every time. Each stage runs only after the previous one succeeded, and
// no stage is ever retried automatically. Nothing here imports a fixture; there
// is no request-time fallback.
//
// These transfers do not enter `upload-store`'s queue, so they are not subject
// to its concurrency budget — unchanged by the move onto the seam, which owns
// transport rather than queueing.

/** The single tag that anchors an uploaded Campaign File. No `deliverable_type` is set at upload time. */
const CAMPAIGN_UPLOAD_CLASSIFICATION = {
	objectType: ClassifiedObjectType.FILE,
	axisCode: AxisCode.FILE_TYPE,
	valueCode: FileType.DELIVERABLE,
} as const

/**
 * A fresh key per full attempt, omitted entirely when `crypto.randomUUID` is
 * unavailable — the backend expects a UUID, so no non-UUID fallback is sent
 * (unlike a purely client-side tracking id, which may fall back freely).
 *
 * Because the key is minted once per full attempt and never reused across
 * attempts, the only replay it can produce is a redelivery of one request whose
 * payload is identical by construction. The stricter server-side rule — a key
 * presented with different creation identity is refused — is therefore
 * unreachable from here.
 */
function newIdempotencyKey(): string | undefined {
	return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : undefined
}

/**
 * Every check that can reject a staged File before any HTTP request, in the
 * order the user should hear about them.
 *
 * `contentType` is the RESOLVED type, not the browser's raw value, and it is
 * resolved once by the caller so the same value is checked here and sent
 * onward. Checking the raw value here while the selection gate checked the
 * resolved one let a file pass selection and fail at this boundary — a `.psd`
 * on Safari, which reports no type at all.
 */
function preValidate(file: CampaignUploadInput, contentType: string): CampaignUploadOutcome | undefined {
	// An input-invariant violation, not a user error: the declared size becomes
	// the File's immutable `declaredFileSize`, so it has to be the size of the
	// bytes actually being sent. The one production construction site reads both
	// off the same browser File; this is what keeps that true.
	if (file.size !== file.bytes.size) {
		return { kind: 'upload-failed', step: 'initiation', reason: 'failure' }
	}
	// Before the type check, deliberately: a zero-byte file with an unrecognised
	// type is empty first, and that is what the user can act on. The threshold is
	// the shared one; only the wording is Campaign's own.
	if (isEmptyUploadSize(file.size)) {
		return { kind: 'upload-failed', step: 'initiation', reason: 'empty-file' }
	}
	if (!(FILE_ALLOWED_CONTENT_TYPES as readonly string[]).includes(contentType)) {
		return { kind: 'upload-failed', step: 'initiation', reason: 'invalid-file-type' }
	}
	if (file.size > FILE_MAX_SIZE_BYTES) {
		return { kind: 'upload-failed', step: 'initiation', reason: 'file-too-large' }
	}
	return undefined
}

/**
 * The browser `File` the seam needs, from the transport-neutral `Blob` the port
 * carries. The seam derives the backend `fileName` from `file.name` and the
 * declared size from `file.size`, so the caller's own `File` is reused only
 * when it already carries exactly those; anything else is reconstructed with
 * the RESOLVED content type, never the raw browser value.
 */
function asBrowserFile(input: CampaignUploadInput, contentType: string): File {
	const { bytes, name, size } = input
	if (bytes instanceof File && bytes.name === name && bytes.size === size) return bytes
	return new File([bytes], name, { type: contentType })
}

/** Which mapper turns a transport error into a page-renderable reason, per stage. */
const FAILURE_MAPPERS: Record<'initiation' | 'transfer' | 'confirmation', (error: unknown) => CampaignUploadOutcome> = {
	initiation: (error) => ({ kind: 'upload-failed', step: 'initiation', reason: mapUploadInitiationFailure(error) }),
	transfer: (error) => ({ kind: 'upload-failed', step: 'transfer', reason: mapUploadTransferFailure(error) }),
	confirmation: (error) => ({
		kind: 'upload-failed',
		step: 'confirmation',
		reason: mapUploadConfirmationFailure(error),
	}),
}

/** Stages 1: create, transfer real bytes, confirm — all through the seam. Resolves the confirmed File id. */
async function transferAndConfirm(
	file: CampaignUploadInput,
	contentType: string,
	options: CampaignUploadExecutionOptions | undefined,
): Promise<{ fileId: string } | CampaignUploadOutcome> {
	const idempotencyKey = newIdempotencyKey()

	// How far the seam got, tracked from the milestones it already reports —
	// `onFileCreated` means the transport is ready to send bytes, `onConfirming`
	// that they are on the server. No fourth callback is needed to place a
	// failure, and none is added.
	let reached: Extract<CampaignUploadFailureStep, 'initiation' | 'transfer' | 'confirmation'> = 'initiation'

	options?.onTransientState?.('upload-pending')
	try {
		const confirmed = await uploaderService.execute(
			{
				file: asBrowserFile(file, contentType),
				contentType,
				...(idempotencyKey ? { idempotencyKey } : {}),
				signal: options?.signal,
			},
			{
				onFileCreated: () => {
					reached = 'transfer'
				},
				onProgress: (percent) => options?.onProgress?.(percent),
				onConfirming: () => {
					reached = 'confirmation'
				},
			},
		)
		options?.onTransientState?.('uploaded')
		return { fileId: confirmed.fileId }
	} catch (error) {
		return FAILURE_MAPPERS[reached](error)
	}
}

/** Stage 3, on its own — the whole of an association-only retry. */
async function associate(campaignId: string, fileId: string, fileName: string): Promise<CampaignUploadOutcome> {
	try {
		await campaignService.associateFiles(campaignId, [fileId])
	} catch {
		return { kind: 'association-failed', operationToken: fileId, fileName }
	}
	return { kind: 'success', fileId }
}

export const campaignUploadHttpAdapter: CampaignUploadPort = {
	async uploadAndAssociate(
		campaignId: string,
		file: CampaignUploadInput,
		options?: CampaignUploadExecutionOptions,
	): Promise<CampaignUploadOutcome> {
		// Resolved once: the same value gates the request, is sent as the File's
		// declared content type, and signs the presigned URL the transfer must
		// match. Resolving it in more than one place is how those three drift.
		const contentType = resolveContentType(file.name, file.contentType)

		const rejected = preValidate(file, contentType)
		if (rejected) return rejected

		const transferred = await transferAndConfirm(file, contentType, options)
		if ('kind' in transferred) return transferred
		const { fileId } = transferred

		try {
			await classificationApi.classifyBatch([{ ...CAMPAIGN_UPLOAD_CLASSIFICATION, objectId: fileId }])
		} catch (error) {
			// Never `association-failed`: an unclassified File has no anchor, so an
			// association-only retry could not recover it — only a full retry can.
			return { kind: 'upload-failed', step: 'classification', reason: mapUploadClassificationFailure(error) }
		}

		options?.onTransientState?.('association-pending')
		return associate(campaignId, fileId, file.name)
	},

	async retryAssociation(
		campaignId: string,
		operationToken: string,
		options?: Pick<CampaignUploadExecutionOptions, 'onTransientState' | 'signal'>,
	): Promise<CampaignUploadOutcome> {
		options?.onTransientState?.('association-retry-pending')
		// `operationToken` IS the confirmed, classified File id — nothing before
		// association is repeated, and no File re-selection is required. Success
		// carries only that id; the gallery's authoritative representation arrives
		// through the Files query's invalidation and refetch, not from this result.
		return associate(campaignId, operationToken, operationToken)
	},
}
